import React, { useState } from 'react';
import { TextField, Button, Box, Typography, Container, Grid, InputAdornment, IconButton, Alert } from '@mui/material';
import PasswordRequirements from '../PasswordRequirements';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import api from '../../utils/axiosConfig';
import { useNavigate, Link as RouterLink } from 'react-router-dom';

function CreateAccount() {
    const [showPassword, setShowPassword] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [formData, setFormData] = useState({
        email: '',
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
    });
    const navigate = useNavigate();

    const handleAccountCreation = async (e) => {
        e.preventDefault();
        const passwordValidationResult = validatePassword(formData.newPassword);
        if (passwordValidationResult !== true) {
            setErrorMessage(passwordValidationResult);
            return;
        }

        if (formData.newPassword !== formData.confirmPassword) {
            setErrorMessage("Passwords do not match");
            return;
        }

        try {
            // First verify the unique ID
            // For account creation, we don't want to send the auth token
            const response = await api.post(`/api/caregiver/finalizeCreateAccount`, { ...formData, email: formData.email.trim() }, {
                headers: {
                    "Authorization": '' // Override the default Authorization header
                }
            });
            if (response.status === 200) {
                navigate('/login');
            }
        } catch (error) {
            // Never log Axios errors: they contain the temporary and new passwords.
            if (error.response?.status === 401) {
                setErrorMessage('Unable to finalize this account. Check the email and temporary password from your invitation. If you already completed setup, use Login or Forgot Password.');
            } else if (!error.response) {
                setErrorMessage('Unable to connect. Check your connection and try again.');
            } else {
                setErrorMessage('Unable to finalize this account. Please try again later.');
            }
        }
    };

    const validatePassword = (password) => {
        const lengthCheck = /^.{8,30}$/;
        const digitCheck = /\d/;
        const upperCaseCheck = /[A-Z]/;
        const lowerCaseCheck = /[a-z]/;
        const specialCharacterCheck = /[!@#$%^&*(),.?":{}|<>]/;
        let err = '';
        if (!lengthCheck.test(password)) {
            err += 'Password must be between 8 and 30 characters in length. ';
        }
        if (!digitCheck.test(password)) {
            err += 'Password must include at least one digit. ';
        }
        if (!upperCaseCheck.test(password) || !lowerCaseCheck.test(password)) {
            err += 'Password must include both upper and lower case characters. ';
        }
        if (!specialCharacterCheck.test(password)) {
            err += 'Password must include at least one special character.';
        }
        if (!err) {
            return true;  // Return true if all checks pass
        } else {
            return err;
        }
    };

    return (
        <Container maxWidth="xs">
            <Box sx={{ mt: 5 }}>
                <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: '90vh', margin: 0 }}>
                    <Grid item>
                        <Box component="form" onSubmit={handleAccountCreation} sx={{ padding: '20px', textAlign: 'center', maxWidth: '300px', margin: '0 auto' }}>
                            <Button component={RouterLink} to="/login" startIcon={<ArrowBackIcon />}
                                sx={{ display: 'flex', width: 'fit-content', minHeight: 48, mb: 2 }}>
                                Back to Login
                            </Button>
                            <Typography variant="h5" component="h1" gutterBottom>
                                Finalize Creation
                            </Typography>
                            <TextField
                                id="create-email"
                                name="email"
                                label="Email"
                                autoComplete="username"
                                inputProps={{ inputMode: 'email', autoCapitalize: 'none', autoCorrect: 'off', spellCheck: false }}
                                fullWidth
                                margin="normal"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            />
                            <Box sx={{ position: 'relative' }}>
                                <TextField
                                    id="create-oldPassword"
                                    name="oldPassword"
                                    autoComplete="current-password"
                                    label="Temporary Password"
                                    type={showPassword ? 'text' : 'password'}  // Toggle input type based on state
                                    fullWidth
                                    margin="normal"
                                    value={formData.oldPassword}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    type="button"
                                                    aria-label={showPassword ? 'Hide passwords' : 'Show passwords'}
                                                    sx={{ minWidth: 48, minHeight: 48 }}
                                                    onClick={() => setShowPassword(!showPassword)}  // Toggle showPassword state
                                                    edge="end"
                                                >
                                                    {!showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                    onChange={(e) => setFormData({ ...formData, oldPassword: e.target.value })}
                                />
                            </Box>
                            <Box sx={{ position: 'relative' }}>
                                <TextField
                                    id="create-newPassword"
                                    name="newPassword"
                                    inputProps={{ 'aria-describedby': 'create-password-requirements' }}
                                    autoComplete="new-password"
                                    label="New Password"
                                    type={showPassword ? 'text' : 'password'}  // Toggle input type based on state
                                    fullWidth
                                    margin="normal"
                                    value={formData.newPassword}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    type="button"
                                                    aria-label={showPassword ? 'Hide passwords' : 'Show passwords'}
                                                    sx={{ minWidth: 48, minHeight: 48 }}
                                                    onClick={() => setShowPassword(!showPassword)}  // Toggle showPassword state
                                                    edge="end"
                                                >
                                                    {!showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                    onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                                />
                            </Box>
                            <TextField
                                id="create-confirmPassword"
                                name="confirmPassword"
                                autoComplete="new-password"
                                label="Confirm Password"
                                type={showPassword ? 'text' : 'password'}  // Toggle input type based on state
                                fullWidth
                                margin="normal"
                                value={formData.confirmPassword}
                                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                                required
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                type="button"
                                                aria-label={showPassword ? 'Hide passwords' : 'Show passwords'}
                                                sx={{ minWidth: 48, minHeight: 48 }}
                                                onClick={() => setShowPassword(!showPassword)}  // Toggle showPassword state
                                                edge="end"
                                            >
                                                {!showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <PasswordRequirements id="create-password-requirements" />
                            {errorMessage && <Alert severity="error" sx={{ my: 2 }}>{errorMessage}</Alert>}
                            <Button
                                variant="contained"
                                color="primary"
                                type="submit"
                                fullWidth
                                sx={{ marginTop: '16px', minHeight: 48 }}
                            >
                                Create Account
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </Container>
    );
}

export default CreateAccount;
